/**
 * Строка списка со свайп-действиями — как в Telegram: движение влево
 * открывает кнопки («без звука», «в архив»), длинный свайп сразу выполняет
 * главное действие, касание в другом месте закрывает открытую строку.
 *
 * Только для касания: мышью те же действия остаются в меню правой кнопки.
 * Строка-ссылка внутри не знает о жесте — нажатие после свайпа гасится здесь.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import { VelocityTracker, haptic, isTouchLike, rubberBand } from '@shared/lib/motion'
import { Icon, cx, type IconName } from '@shared/ui'

import styles from './chats.module.css'

export interface SwipeAction {
  key: string
  label: string
  icon: IconName
  tone: 'accent' | 'warning'
  onSelect: () => void
}

interface SwipeableRowProps {
  actions: readonly SwipeAction[]
  /** Длинный свайп (больше половины ширины) — главное действие. */
  onFullSwipe?: () => void
  enabled?: boolean
  /** Разделитель над строкой — от края текста, как в Telegram. */
  divider?: boolean
  children: ReactNode
}

/** Ширина одной кнопки действия. */
const ACTION_WIDTH = 76
const ACTIVATE_PX = 10
/** Доля ширины строки, после которой свайп считается «длинным». */
const FULL_RATIO = 0.55
/** px/мс — быстрый взмах открывает или закрывает без доводки до половины. */
const FLICK_VELOCITY = 0.3
const SETTLE_MS = 240

interface DragState {
  pointerId: number
  startX: number
  startY: number
  base: number
  active: boolean
  x: number
  full: boolean
  width: number
  tracker: VelocityTracker
  frame: number
}

/** Открыта может быть только одна строка. */
let closeOpenRow: (() => void) | null = null

export function SwipeableRow({
  actions,
  onFullSwipe,
  enabled = true,
  divider = false,
  children,
}: SwipeableRowProps): React.ReactElement {
  const rootRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const suppressClickRef = useRef(false)
  const [revealed, setRevealed] = useState(false)
  const [open, setOpen] = useState(false)
  const [full, setFull] = useState(false)

  const revealWidth = actions.length * ACTION_WIDTH

  const place = useCallback(
    (x: number, animate: boolean): void => {
      const content = contentRef.current
      const panel = actionsRef.current
      const transition = animate ? `transform ${SETTLE_MS}ms var(--ease-ios)` : 'none'
      if (content) {
        content.style.transition = transition
        content.style.transform = `translateX(${x}px)`
      }
      if (panel) {
        panel.style.transition = animate ? `width ${SETTLE_MS}ms var(--ease-ios)` : 'none'
        panel.style.width = `${Math.max(revealWidth, -x)}px`
      }
    },
    [revealWidth],
  )

  const close = useCallback(
    (animate = true): void => {
      place(0, animate)
      setOpen(false)
      setFull(false)
      if (closeOpenRow === close) closeOpenRow = null
      setTimeout(() => setRevealed(false), animate ? SETTLE_MS : 0)
    },
    [place],
  )

  const openRow = useCallback((): void => {
    if (closeOpenRow && closeOpenRow !== close) closeOpenRow()
    closeOpenRow = close
    place(-revealWidth, true)
    setOpen(true)
    setFull(false)
    setRevealed(true)
  }, [close, place, revealWidth])

  useEffect(
    () => () => {
      if (closeOpenRow === close) closeOpenRow = null
    },
    [close],
  )

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!enabled || !isTouchLike(event) || !event.isPrimary || dragRef.current) return
    const root = rootRef.current
    if (!root) return

    // Открыта другая строка — касание закрывает её, а собственный жест может начаться.
    if (closeOpenRow && closeOpenRow !== close) closeOpenRow()

    const tracker = new VelocityTracker()
    tracker.push(event.clientX, event.clientY)
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      base: open ? -revealWidth : 0,
      active: false,
      x: open ? -revealWidth : 0,
      full: false,
      width: root.clientWidth,
      tracker,
      frame: 0,
    }
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return

    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    drag.tracker.push(event.clientX, event.clientY)

    if (!drag.active) {
      if (Math.abs(dy) > ACTIVATE_PX && Math.abs(dy) > Math.abs(dx)) {
        dragRef.current = null
        return
      }
      if (Math.abs(dx) < ACTIVATE_PX) return
      // Закрытую строку тянут только влево: вправо тянуть нечего.
      if (!open && dx > 0) {
        dragRef.current = null
        return
      }
      drag.active = true
      suppressClickRef.current = true
      setRevealed(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    }

    let x = drag.base + dx
    if (x > 0) x = rubberBand(x, 24)
    drag.x = x

    const isFull = Boolean(onFullSwipe) && -x > drag.width * FULL_RATIO
    if (isFull !== drag.full) {
      drag.full = isFull
      setFull(isFull)
      if (isFull) haptic('light')
    }

    cancelAnimationFrame(drag.frame)
    drag.frame = requestAnimationFrame(() => place(x, false))
  }

  const finish = (event: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current
    if (!drag || event.pointerId !== drag.pointerId) return
    dragRef.current = null
    cancelAnimationFrame(drag.frame)

    if (!drag.active) {
      // Нажатие по открытой строке закрывает её, а не открывает диалог.
      if (open) {
        suppressClickRef.current = true
        close()
      }
      return
    }

    const velocity = drag.tracker.velocity().x

    if (drag.full && onFullSwipe) {
      // Строка улетает влево, действие выполняется сразу — как архив в Telegram.
      place(-drag.width - 8, true)
      setTimeout(() => {
        onFullSwipe()
        close(false)
      }, SETTLE_MS)
      return
    }

    const shouldOpen = velocity < -FLICK_VELOCITY || (velocity < FLICK_VELOCITY && -drag.x > revealWidth / 2)
    if (shouldOpen) openRow()
    else close()
  }

  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!suppressClickRef.current) return
    suppressClickRef.current = false
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <div
      ref={rootRef}
      className={cx(styles['swipe'], divider && styles['swipeDivider'], full && styles['swipeFull'])}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
      onPointerCancel={finish}
      onClickCapture={onClickCapture}
    >
      <div
        ref={actionsRef}
        className={styles['swipeActions']}
        style={{ width: revealWidth, visibility: revealed ? 'visible' : 'hidden' }}
        aria-hidden={!open}
      >
        {actions.map((action, index) => (
          <button
            key={action.key}
            type="button"
            tabIndex={open ? 0 : -1}
            className={cx(
              styles['swipeAction'],
              action.tone === 'warning' ? styles['swipeActionWarning'] : styles['swipeActionAccent'],
              index === actions.length - 1 && styles['swipeActionPrimary'],
            )}
            onClick={() => {
              close()
              action.onSelect()
            }}
          >
            <Icon name={action.icon} size={22} />
            <span>{action.label}</span>
          </button>
        ))}
      </div>

      <div ref={contentRef} className={styles['swipeContent']}>
        {children}
      </div>
    </div>
  )
}
